import React, { useState, useEffect, useRef } from 'react'
import { PageLayout } from '../../components/layout/PageLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { ToastContainer } from '../../components/ui/Toast'
import { useToast } from '../../hooks/useToast'
import { ContinentsService } from '../../services/api/continentsService'
import { Continent, Country } from '../../types/continent'
import { logger } from '../../utils/logger'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

export const ContinentsPage: React.FC = () => {
  const [continents, setContinents] = useState<Continent[]>([])
  const [loading, setLoading] = useState(true)
  const { toasts, showToast, removeToast } = useToast()
  const hasFetched = useRef(false)

  // Dialog states
  const [showContinentDialog, setShowContinentDialog] = useState(false)
  const [showCountryDialog, setShowCountryDialog] = useState(false)
  const [showDeleteContinentDialog, setShowDeleteContinentDialog] = useState(false)
  const [showDeleteCountryDialog, setShowDeleteCountryDialog] = useState(false)

  // Form states
  const [continentName, setContinentName] = useState('')
  const [countryName, setCountryName] = useState('')
  const [selectedContinent, setSelectedContinent] = useState<Continent | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [continentToDelete, setContinentToDelete] = useState<Continent | null>(null)
  const [countryToDelete, setCountryToDelete] = useState<{ continent: Continent, country: Country } | null>(null)

  useEffect(() => {
    // Skip if already fetched (StrictMode protection in dev)
    if (hasFetched.current) return
    hasFetched.current = true
    
    fetchContinents()
  }, [])

  const fetchContinents = async () => {
    setLoading(true)
    try {
      const data = await ContinentsService.getContinents()
      // Sort continents alphabetically and sort countries within each continent
      const sortedData = [...data]
        .map(continent => ({
          ...continent,
          countries: [...continent.countries].sort((a, b) => 
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          )
        }))
        .sort((a, b) => 
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        )
      setContinents(sortedData)
    } catch (error) {
      logger.error('Error fetching continents:', error)
      showToast('Erreur lors du chargement des continents', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Continent CRUD operations
  const handleAddContinent = () => {
    setSelectedContinent(null)
    setContinentName('')
    setShowContinentDialog(true)
  }

  const handleEditContinent = (continent: Continent) => {
    setSelectedContinent(continent)
    setContinentName(continent.name)
    setShowContinentDialog(true)
  }

  const handleDeleteContinent = (continent: Continent) => {
    setContinentToDelete(continent)
    setShowDeleteContinentDialog(true)
  }

  const submitContinent = async () => {
    if (!continentName.trim()) return

    try {
      if (selectedContinent?.id) {
        // Update existing
        const updated = await ContinentsService.updateContinent({
          ...selectedContinent,
          name: continentName.trim()
        })
        setContinents(continents.map(c => 
          c.id === updated.id ? updated : c
        ))
        showToast('Continent modifié avec succès', 'success')
      } else {
        // Create new
        const created = await ContinentsService.createContinent({
          name: continentName.trim(),
          countries: []
        })
        setContinents([...continents, created].sort((a, b) => 
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        ))
        showToast('Continent créé avec succès', 'success')
      }
      setShowContinentDialog(false)
      setSelectedContinent(null)
      setContinentName('')
    } catch (error) {
      logger.error('Error saving continent:', error)
      showToast('Erreur lors de l\'enregistrement du continent', 'error')
    }
  }

  const confirmDeleteContinent = async () => {
    if (!continentToDelete?.id) return

    try {
      await ContinentsService.deleteContinent(continentToDelete.id)
      setContinents(continents.filter(c => c.id !== continentToDelete.id))
      showToast('Continent supprimé avec succès', 'success')
      setShowDeleteContinentDialog(false)
      setContinentToDelete(null)
    } catch (error) {
      logger.error('Error deleting continent:', error)
      // Check if continent has countries
      const hasCountries = continentToDelete.countries && continentToDelete.countries.length > 0
      showToast(
        hasCountries 
          ? 'Impossible de supprimer le continent car il contient encore des pays. Veuillez d&apos;abord supprimer tous les pays.'
          : 'Erreur lors de la suppression du continent.',
        'error'
      )
    }
  }

  // Country CRUD operations
  const handleAddCountry = (continent: Continent) => {
    setSelectedContinent(continent)
    setSelectedCountry(null)
    setCountryName('')
    setShowCountryDialog(true)
  }


  const handleDeleteCountry = (continent: Continent, country: Country) => {
    setCountryToDelete({ continent, country })
    setShowDeleteCountryDialog(true)
  }

  const submitCountry = async () => {
    if (!selectedContinent?.id || !countryName.trim()) return

    try {
      if (selectedCountry?.id) {
        // Update existing country locally
        const updatedCountry = {
          ...selectedCountry,
          name: countryName.trim()
        }
        
        await ContinentsService.updateCountry(selectedContinent.id, updatedCountry)
        
        // Update local state
        setContinents(continents.map(c => {
          if (c.id === selectedContinent.id) {
            return {
              ...c,
              countries: c.countries
                .map(country => country.id === selectedCountry.id ? updatedCountry : country)
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            }
          }
          return c
        }))
        
        showToast('Pays modifié avec succès', 'success')
      } else {
        // Create new country
        const newCountry = await ContinentsService.addCountry(selectedContinent.id, {
          name: countryName.trim()
        })
        
        // Update local state by adding the new country and sorting
        setContinents(continents.map(c => {
          if (c.id === selectedContinent.id) {
            return {
              ...c,
              countries: [...c.countries, newCountry].sort((a, b) => 
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
              )
            }
          }
          return c
        }))
        
        showToast('Pays ajouté avec succès', 'success')
      }
      
      setShowCountryDialog(false)
      setSelectedContinent(null)
      setSelectedCountry(null)
      setCountryName('')
    } catch (error) {
      logger.error('Error saving country:', error)
      showToast('Erreur lors de l\'enregistrement du pays', 'error')
    }
  }

  const confirmDeleteCountry = async () => {
    if (!countryToDelete?.continent.id || !countryToDelete?.country.id) return

    try {
      await ContinentsService.deleteCountry(
        countryToDelete.continent.id,
        countryToDelete.country.id
      )
      
      // Update local state
      setContinents(continents.map(c => {
        if (c.id === countryToDelete.continent.id) {
          return {
            ...c,
            countries: c.countries.filter(country => country.id !== countryToDelete.country.id)
          }
        }
        return c
      }))
      
      showToast('Pays supprimé avec succès', 'success')
      setShowDeleteCountryDialog(false)
      setCountryToDelete(null)
    } catch (error) {
      logger.error('Error deleting country:', error)
      showToast(
        'Erreur lors de la suppression du pays.',
        'error'
      )
    }
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout maxWidth="4xl">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Continents</h1>
        <Button
          onClick={handleAddContinent}
          variant="primary"
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Nouveau
        </Button>
      </div>

      {/* Continents List */}
      {continents.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">Aucun continent</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {continents.map((continent) => (
            <Card key={continent.id} className="p-6">
              {/* Continent Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-medium text-gray-900 dark:text-white">
                  {continent.name}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditContinent(continent)}
                    className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteContinent(continent)}
                    className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Countries */}
              <div className="flex flex-wrap gap-2">
                {continent.countries.map((country) => (
                  <div
                    key={country.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                  >
                    <span>{country.name}</span>
                    <button
                      onClick={() => handleDeleteCountry(continent, country)}
                      className="ml-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                
                {/* Add Country Button */}
                <button
                  onClick={() => handleAddCountry(continent)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-sk-black text-white dark:bg-white dark:text-sk-black rounded-full text-sm hover:opacity-90 transition-opacity"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  <span>Ajouter un pays</span>
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Continent Dialog */}
      {showContinentDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
              {selectedContinent ? 'Modifier le continent' : 'Nouveau continent'}
            </h3>
            <div className="mb-4">
              <label htmlFor="continent-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Nom <span className="text-red-500">*</span>
              </label>
              <Input
                id="continent-name"
                type="text"
                value={continentName}
                onChange={(e) => setContinentName(e.target.value)}
                placeholder="Entrez le nom du continent"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => setShowContinentDialog(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={submitContinent}
                disabled={!continentName.trim()}
              >
                {selectedContinent ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Country Dialog */}
      {showCountryDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
              {selectedCountry ? 'Modifier le pays' : 'Nouveau pays'}
            </h3>
            {selectedContinent && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Continent: <span className="font-medium">{selectedContinent.name}</span>
              </p>
            )}
            <div className="mb-4">
              <label htmlFor="country-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Nom <span className="text-red-500">*</span>
              </label>
              <Input
                id="country-name"
                type="text"
                value={countryName}
                onChange={(e) => setCountryName(e.target.value)}
                placeholder="Entrez le nom du pays"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => setShowCountryDialog(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={submitCountry}
                disabled={!countryName.trim()}
              >
                {selectedCountry ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Continent Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteContinentDialog}
        onClose={() => {
          setShowDeleteContinentDialog(false)
          setContinentToDelete(null)
        }}
        onConfirm={confirmDeleteContinent}
        title="Supprimer le continent"
        message={`Êtes-vous sûr de vouloir supprimer ${continentToDelete?.name} ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />

      {/* Delete Country Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteCountryDialog}
        onClose={() => {
          setShowDeleteCountryDialog(false)
          setCountryToDelete(null)
        }}
        onConfirm={confirmDeleteCountry}
        title="Supprimer le pays"
        message={`Êtes-vous sûr de vouloir supprimer ${countryToDelete?.country.name} ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </PageLayout>
  )
}